import React, { useState, useEffect, useMemo } from 'react';
import { API, authFetch } from '../utils/api';

function CompanyListPanel({ user, context, month, selectedCompanyId, onSelect, onSwitch }) {
  var [companies, setCompanies] = useState([]);
  var [showPanel, setShowPanel] = useState(false);
  var [search, setSearch] = useState('');
  var [loading, setLoading] = useState(true);

  var role = user ? user.role : '';
  var shouldShow = role === 'doc_agency' || role === 'admin' || role === '관리자';

  useEffect(function() {
    if (!shouldShow) {
      // check if user has multiple companies
      authFetch(API + '/auth/my-companies').then(function(r) { return r.json(); }).then(function(d) {
        if (d.success && d.companies && d.companies.length > 1) { setShowPanel(true); loadPanel(); }
        else { setShowPanel(false); setLoading(false); }
      }).catch(function() { setShowPanel(false); setLoading(false); });
    } else {
      setShowPanel(true);
      loadPanel();
    }
  }, []);

  useEffect(function() {
    if (showPanel) loadPanel();
  }, [month, context]);

  var loadPanel = function() {
    var url = API + '/dashboard/company-panel?view=' + (context || 'tax');
    if (month) url += '&ym=' + month;
    authFetch(url).then(function(r) { return r.json(); }).then(function(d) {
      setCompanies(d.companies || []);
      if (d.showPanel === false) setShowPanel(false);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  };

  var [switching, setSwitching] = useState(null);

  var handleSelect = function(companyId) {
    var cid = companyId ? parseInt(companyId) || 0 : 0;
    if (cid === 0) { onSelect(''); return; }
    setSwitching(cid);
    authFetch(API + '/auth/switch-company', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId: cid })
    }).then(function(r) { return r.json(); }).then(function(d) {
      setSwitching(null);
      if (d.success) {
        localStorage.setItem('autodoc_token', d.token);
        if (d.user) {
          sessionStorage.setItem('autodoc_user', JSON.stringify(d.user));
          window.dispatchEvent(new CustomEvent('company-switched', { detail: d.user }));
        }
        onSelect(String(cid));
      }
    }).catch(function() { setSwitching(null); });
  };

  var filtered = useMemo(function() {
    if (!search) return companies;
    var q = search.toLowerCase();
    return companies.filter(function(c) {
      return (c.name || '').toLowerCase().indexOf(q) >= 0 || (c.bizNo || '').indexOf(q) >= 0;
    });
  }, [companies, search]);

  if (!showPanel || loading) return null;

  // summary counts
  var totalCount = companies.length;
  var doneCount = 0;
  var noneCount = 0;
  if (context === 'tax') {
    doneCount = companies.filter(function(c) { return c.taxStatus === 'final'; }).length;
    noneCount = companies.filter(function(c) { return c.taxStatus === 'none'; }).length;
  }

  var fmtRevenue = function(v) {
    if (!v) return '-';
    var man = Math.round(v / 10000);
    return man.toLocaleString() + '만원';
  };

  return (
    <div style={{ width: 250, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'fit-content', maxHeight: 'calc(100vh - 160px)' }}>
      {/* Header */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1B3A5C', marginBottom: 8 }}>업체 목록</div>
        {context === 'tax' && (
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
            전체 <b>{totalCount}</b>개 | 완료 <b style={{ color: '#16a34a' }}>{doneCount}</b>개 | 미전송 <b style={{ color: '#dc2626' }}>{noneCount}</b>개
          </div>
        )}
        <input
          type="text"
          placeholder="업체 검색..."
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
        />
      </div>

      {/* Company List */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {/* "All companies" option */}
        <div
          onClick={function() { if(switching!==null)return; handleSelect(''); }}
          style={{
            padding: '10px 14px', cursor: switching!==null?'wait':'pointer', borderBottom: '1px solid #f1f5f9',
            background: selectedCompanyId === '' || !selectedCompanyId ? '#eff6ff' : '#fff',
            fontWeight: selectedCompanyId === '' || !selectedCompanyId ? 700 : 400,
            transition: 'background 0.1s'
          }}>
          <div style={{ fontSize: 13, color: '#1B3A5C' }}>전체 업체</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            {companies.reduce(function(s, c) { return s + (c.carCount || 0); }, 0)}대
          </div>
        </div>

        {filtered.map(function(co) {
          var isSelected = String(selectedCompanyId) === String(co.id);
          return (
            <div
              key={co.id}
              onClick={function() { if(switching!==null)return; handleSelect(co.id); }}
              style={{
                padding: '10px 14px', cursor: switching!==null?'wait':'pointer', borderBottom: '1px solid #f1f5f9',
                background: isSelected ? '#eff6ff' : '#fff',
                transition: 'background 0.1s'
              }}
              onMouseEnter={function(e) { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={function(e) { if (!isSelected) e.currentTarget.style.background = '#fff'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', maxWidth: 160 }}>
                  <span style={{ fontSize: 13, fontWeight: isSelected ? 700 : 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{co.name}</span>
                  {onSwitch && !isSelected && <button onClick={function(e) { e.stopPropagation(); onSwitch(co.id); }} style={{ padding: '1px 6px', borderRadius: 4, border: '1px solid #d0d5dd', background: '#fff', fontSize: 9, color: '#64748b', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>전환</button>}
                </div>
                {/* Status badge by context */}
                {context === 'tax' && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                    background: co.taxStatus === 'final' ? '#dcfce7' : co.taxStatus === 'first' ? '#fef3c7' : '#f1f5f9',
                    color: co.taxStatus === 'final' ? '#166534' : co.taxStatus === 'first' ? '#92400e' : '#94a3b8'
                  }}>
                    {co.taxStatus === 'final' ? '최종완료' : co.taxStatus === 'first' ? '1차전송' : '미전송'}
                  </span>
                )}
                {context === 'invoice' && (
                  <span style={{ fontSize: 10, fontWeight: 600 }}>
                    {co.invoiceReady > 0 && <span style={{ color: '#d97706', marginRight: 4 }}>대기{co.invoiceReady}</span>}
                    {co.invoiceSent > 0 && <span style={{ color: '#16a34a' }}>완료{co.invoiceSent}</span>}
                    {!co.invoiceReady && !co.invoiceSent && <span style={{ color: '#94a3b8' }}>-</span>}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                {co.bizNo ? co.bizNo + ' | ' : ''}
                {context === 'ledger'
                  ? ('차량 ' + (co.carCount || 0) + '대 | ' + fmtRevenue(co.revenue))
                  : (context === 'tax'
                    ? (co.taxCarCount || 0) + '대'
                    : (co.carCount || 0) + '대')
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CompanyListPanel;
